import React, { useMemo, useState } from 'react';

import { MenuItem } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useParams } from 'react-router-dom';
import { InputActionMeta, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import scssVariables from 'styles/variables.module.scss';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useHashmapConverter from 'hooks/useHashmapConverter';
import { useQueryState } from 'hooks/useQueryState';
import useUserHook from 'hooks/useUserHook';

import { useClusterRCFAnalysisStore } from 'store/clusterRCFAnalysis.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationSaveSearchWord } from 'api/mutations/saveSearchWord.mutation';
import { useMutationSavedSearchDelete } from 'api/mutations/savedSearchDelete.mutation';
import { useQueryClustersDataBySearchWord } from 'api/queries/clustersDataBySearchWord.query';
import { useQueryClustersSearchWordsList } from 'api/queries/clustersSearchWordsList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { SelectStyles } from 'components/common/MyCustomSelect/myCustomSelect.style';

import { AdvancedWrapperTooltipContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/components/Filters/SearchByName/components/AdvancedWrapperTooltipContent/AdvancedWrapperTooltipContent';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import {
    CustomizedConfirmDeleteModal,
    CustomizedDropdownMenu,
    CustomizedRoundIcon,
    CustomizedTooltip,
} from 'components/common';
import { compareOptions } from 'components/pages/DurationForecastPage/components/CompareTo/CompareTo';
import classes from 'components/pages/DurationForecastPage/components/DurationForecastFilter/components/CustomClusteringSelection/CustomClusteringSelection.module.scss';

export const CustomClusteringSelection = () => {
    const { mediaLaptop } = scssVariables;
    const isLaptop = useMediaQuery(`(max-width: ${mediaLaptop})`);
    const [compare] = useQueryState('clusterScope');
    const { id } = useParams<{ id: string }>();
    const { idList } = useHashmapConverter(id);
    const { project } = useProjectTreeStore();
    const projectId = project?.id;
    const searchWord = useClusterRCFAnalysisStore((store) => store.searchWord);
    const { update } = useClusterRCFAnalysisStore();
    const { handleSnackBar } = useCustomSnackBar();
    const [searchValue, setSearchValue] = useState<string | undefined>(searchWord);
    const [beforeSaveModal, setBeforeSaveModal] = useState(false);
    const [searchWordId, setSearchWordId] = useState<number | null>(null);
    const [deleteSearchWordModalShow, setDeleteSearchWordModalShow] = useState(false);
    const { customerId } = useUserHook();
    const compareKey = compareOptions.find((item) => item.value === compare)?.value;
    const { data: searchWordsList, refetch } = useQueryClustersSearchWordsList(customerId);
    const mutation = useMutationSaveSearchWord();
    const mutationDeleteSavedSearch = useMutationSavedSearchDelete();
    const getClustersDataBySearchWord = useQueryClustersDataBySearchWord({
        idList: idList,
        projectId: projectId,
        customerId: customerId,
        searchWord: searchWord,
        compare: compareKey,
    });
    const { isLoading } = getClustersDataBySearchWord;

    const handleOnSearch = () => {
        update({ searchWord: searchValue });
    };

    const handleSaveSearch = () => {
        if (searchValue && searchValue.trim().length > 0) {
            setBeforeSaveModal(true);
        }
    };

    const handleSaveSearchFinal = () => {
        mutation.mutate(
            {
                customerId: customerId,
                paFileMetaDataIds: idList,
                projectId: projectId,
                query: searchValue,
            },
            {
                onSuccess: () => {
                    refetch().then(() => {
                        handleSnackBar('Search Saved Successfully', 'success');
                    });
                },
                onError: (error) => {
                    const message = 'Failed To Save Search';
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                },
            },
        );
        setBeforeSaveModal(false);
    };

    const handleModalClose = () => {
        setBeforeSaveModal(false);
    };

    const confirmSavedFilterDeletion = () => {
        const searchValueObj = searchWordsList
            ? searchWordsList.filter((item) => item.clusterName.toLowerCase() === searchValue?.toLowerCase())
            : [];

        searchValueObj.length > 0 && setSearchWordId(searchValueObj[0].id);
        setDeleteSearchWordModalShow(true);
    };

    const handleSearchWordDelete = () => {
        mutationDeleteSavedSearch.mutateAsync(searchWordId).then(() => {
            setDeleteSearchWordModalShow(false);
            setSearchValue(undefined);
            update({ searchWord: undefined });
            refetch().then(() => {
                handleSnackBar('Search Deleted Successfully', 'success');
            });
        });
    };

    const isEnableDelete =
        searchWordsList &&
        searchWordsList.some((item) => item.clusterName.toLowerCase() === searchValue?.toLowerCase());

    const handleOnChange = (value: SingleValue<{ label: string; value: string }>) => {
        if (!value) {
            setSearchValue(undefined);
            update({ searchWord: undefined });
        } else {
            setSearchValue(value.value);
            update({ searchWord: value.value });
        }
    };

    const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
        if (actionMeta.action === 'input-blur' && actionMeta.prevInputValue !== newValue) {
            setSearchValue(actionMeta.prevInputValue);
            update({ searchWord: actionMeta.prevInputValue });
        }
    };

    const selectValue = useMemo(() => {
        return searchValue ? { label: searchValue, value: searchValue } : null;
    }, [searchValue]);

    return (
        <div data-testid={'custom-clustering-container'} className={classes.customClusteringContainer}>
            <h3>Search: </h3>
            <CustomizedTooltip
                tooltipPosition={TooltipPosition.Bottom}
                contentStyles={{
                    maxWidth: isLaptop ? '400px' : '650px',
                }}
                key={'advancedInfo'}
                tooltipContent={<AdvancedWrapperTooltipContent />}
                triggerElement={
                    <CustomizedRoundIcon enableHover={true} size={'24px'}>
                        <Icon name={'info-icon'} size={'14px'} />
                    </CustomizedRoundIcon>
                }
            />
            <div className={classes.searchInputContainer}>
                <CreatableSelect
                    value={selectValue}
                    options={searchWordsList?.map((item) => ({
                        label: item.clusterName,
                        value: item.clusterName,
                    }))}
                    isClearable={true}
                    onChange={handleOnChange}
                    onInputChange={handleInputChange}
                    styles={SelectStyles}
                />
            </div>

            <Button size={'medium'} variant={'primary'} onClick={handleOnSearch} isLoading={isLoading}>
                Search
            </Button>

            <CustomizedDropdownMenu
                disabled={searchValue?.trim().length === 0}
                triggerElement={
                    <CustomizedRoundIcon size={'30px'} disabled={searchValue?.trim().length === 0}>
                        <Icon name={'three-dot-icon'} />
                    </CustomizedRoundIcon>
                }
            >
                <MenuItem onClick={handleSaveSearch}>Save Search</MenuItem>
                {isEnableDelete && <MenuItem onClick={confirmSavedFilterDeletion}>Delete</MenuItem>}
            </CustomizedDropdownMenu>

            <Modal
                onClose={handleModalClose}
                open={beforeSaveModal}
                width={'45rem'}
                title={'Save Search'}
                actions={
                    <>
                        <Button size={'medium'} variant={'secondary'} onClick={handleModalClose}>
                            Cancel
                        </Button>
                        <Button size={'medium'} variant={'primary'} onClick={handleSaveSearchFinal}>
                            Save
                        </Button>
                    </>
                }
            >
                <div className={classes.saveFilterModalContainer}>
                    <div className={classes.searchValue}>{searchValue}</div>
                </div>
            </Modal>

            <CustomizedConfirmDeleteModal
                isShowModal={deleteSearchWordModalShow}
                hideModal={() => {
                    setDeleteSearchWordModalShow(false);
                }}
                text={'Are you sure you want to delete this Search?'}
                onConfirm={handleSearchWordDelete}
                isLoading={mutationDeleteSavedSearch.isLoading}
            />
        </div>
    );
};
